import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Input, Row, Space } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { CreateCashier } from "@/events";
import { CashiersStore, useGlobalStore } from "@/stores";
import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import { GeneralSelectorErrors } from "@/ui/_common_/general-selector-errors";
import Page from "@/ui/_common_/page";
import Selector from "@/ui/_common_/selector";

import { CashierCreateModal } from "./cashier-create-modal";
import CashiersTable from "./cashiers-table";
import { EditLimit } from "./edit-limit";

function Cashiers() {
  const globalStore = useGlobalStore();
  const [cashiersStore] = useState(() => new CashiersStore(globalStore));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => cashiersStore.onMount(), []);

  const intl = useIntl();
  const { eventBusService, permissionsStore } = globalStore;

  const {
    clientSelectorStore,
    agentSelectorStore,
    subagentSelectorStore,
    hallSelectorStore,
  } = cashiersStore.generalSelectorsStore;

  return (
    <Page
      title={intl.formatMessage({ defaultMessage: "Cashiers" })}
      isContentLoading={!cashiersStore.generalSelectorsStore.isReady}
    >
      <CashierCreateModal />
      <EditLimit.Modal />

      {(permissionsStore.has("SelectClient") ||
        permissionsStore.has("SelectAgent") ||
        permissionsStore.has("SelectSubagent") ||
        permissionsStore.has("SelectHall")) && (
        <Card size="small">
          <Row gutter={[12, 12]} wrap={true}>
            {permissionsStore.has("SelectClient") && (
              <Col xs={24} md={8} lg={6} xl={4}>
                <Selector
                  store={clientSelectorStore}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Select client",
                  })}
                />
              </Col>
            )}
            {permissionsStore.has("SelectAgent") &&
              !!clientSelectorStore.selectedId && (
                <Col xs={24} md={8} lg={6} xl={4}>
                  <Selector
                    store={agentSelectorStore}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Select an agent",
                    })}
                  />
                </Col>
              )}
            {permissionsStore.has("SelectSubagent") && (
              <Col xs={24} md={8} lg={6} xl={4}>
                <Selector
                  store={subagentSelectorStore}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Select subagent",
                  })}
                />
              </Col>
            )}
            {permissionsStore.has("SelectHall") &&
              (clientSelectorStore.selectedId ||
                agentSelectorStore.selectedId) && (
                <Col xs={24} md={8} lg={6} xl={4}>
                  <Selector
                    store={hallSelectorStore}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Select shop",
                    })}
                  />
                </Col>
              )}
          </Row>
        </Card>
      )}
      {!!hallSelectorStore.selectedId && (
        <Card size="small">
          <Row gutter={[16, 16]} wrap={true}>
            {permissionsStore.has("ViewDeletedCashiers") && (
              <Col style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  onChange={(event) => {
                    cashiersStore.includeDeleted = event.target.checked;
                  }}
                >
                  {intl.formatMessage({ defaultMessage: "Include deleted" })}
                </Checkbox>
              </Col>
            )}
            <Col
              xs={24}
              sm={10}
              md={8}
              lg={6}
              xl={4}
              style={{ marginLeft: "auto" }}
            >
              <Input.Search
                placeholder={intl.formatMessage({
                  defaultMessage: "Cashier ID or username",
                })}
                allowClear
                defaultValue={cashiersStore.filterQuery.searchQuery}
                onSearch={(searchQuery) => {
                  cashiersStore.filterQuery.setSearchQuery(searchQuery);
                  cashiersStore.filter();
                }}
              />
            </Col>
            <Col>
              <Button
                onClick={() => {
                  eventBusService.publish(
                    new CreateCashier({
                      hallId: hallSelectorStore.selectedId!,
                    }),
                  );
                }}
                icon={<PlusOutlined />}
              >
                {intl.formatMessage({ defaultMessage: "Create" })}
              </Button>
            </Col>
          </Row>
        </Card>
      )}
      <Card size="small">
        <Space direction="vertical">
          <GeneralSelectorErrors store={cashiersStore.generalSelectorsStore} />
          <ErrorsFormatter queries={[cashiersStore.filterQuery]} />
          <CashiersTable cashiersStore={cashiersStore} />
        </Space>
      </Card>
    </Page>
  );
}

export default observer(Cashiers);
